import { Trash2 } from "lucide-react";
import React from "react";
import { StockOverrideRecord } from "../../../../types/historicalOverrides";
import { formatUtc } from "./types";

interface ExistingOverridesProps {
  overrides: StockOverrideRecord[];
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onDeleteAll: () => void;
}

const ExistingOverrides: React.FC<ExistingOverridesProps> = ({
  overrides,
  onEdit,
  onDelete,
  onDeleteAll,
}) => {
  const thCls =
    "px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400";
  const tdCls = "px-3 py-1.5 text-xs font-mono text-gray-900 dark:text-white";

  if (overrides.length === 0) {
    return (
      <div
        data-testid="existing-overrides"
        className="flex-1 flex items-center justify-center py-16"
      >
        <span className="text-sm text-gray-400 dark:text-gray-500">
          No overrides for this contract.
        </span>
      </div>
    );
  }

  return (
    <div data-testid="existing-overrides" className="flex flex-col h-full">
      <div className="flex items-center justify-end px-4 py-2 border-b border-gray-200 dark:border-rp-separator">
        <button
          type="button"
          onClick={onDeleteAll}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors"
        >
          <Trash2 className="w-3 h-3" />
          Delete all ({overrides.length})
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        <table className="w-full text-left">
          <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-rp-surface-raised">
            <tr className="border-b border-gray-200 dark:border-rp-separator">
              <th className={thCls}>ID</th>
              <th className={thCls}>Type</th>
              <th className={thCls}>Source</th>
              <th className={thCls}>Start</th>
              <th className={thCls}>End</th>
              <th className={thCls}>Factor</th>
              <th className={thCls}>Absolute</th>
              <th className={thCls}>Reason</th>
              <th className={thCls}>Status</th>
              <th className={thCls} style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {overrides.map((ov) => (
              <tr
                key={ov.id}
                data-testid={`override-card-${ov.id}`}
                className="border-b border-gray-100 dark:border-rp-separator/50 hover:bg-gray-50 dark:hover:bg-rp-surface-raised/50 transition-colors"
              >
                <td className={tdCls}>#{ov.id}</td>
                <td className={tdCls}>{ov.adjustmentType}</td>
                <td className={tdCls}>{ov.source}</td>
                <td className={tdCls}>{formatUtc(ov.startAtUtc)}</td>
                <td className={tdCls}>{ov.endAtUtc ? formatUtc(ov.endAtUtc) : "open"}</td>
                <td className={tdCls}>{ov.adjustmentFactor ?? "--"}</td>
                <td className={tdCls}>{ov.adjustmentAbsolute ?? "--"}</td>
                <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 truncate max-w-[160px]">
                  {ov.reason || "--"}
                </td>
                <td className="px-3 py-1.5">
                  {ov.isActive ? (
                    <span className="text-[10px] font-semibold text-green-600 dark:text-green-500">Active</span>
                  ) : (
                    <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500">Inactive</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onEdit(ov.id)}
                      className="px-2 py-0.5 text-[10px] font-semibold rounded bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(ov.id)}
                      className="p-1 rounded text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      aria-label={`Delete override ${ov.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExistingOverrides;
